import React, { type KeyboardEvent, type MouseEvent, useMemo, useCallback } from 'react';
import { styled } from '@compiled/react';
import FocusLock from 'react-focus-lock';
import Button from '@atlaskit/button';
import { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronLeftIcon from '@atlaskit/icon/glyph/chevron-left';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX } from '../../../../../../common/constants';
import type { Field } from '../../../../../../common/constants/fields';
import type { ThirdPartyFieldMapping } from '../../../../../../common/types';
import { DropdownSectionHeader, DropdownSectionWrapper } from '../../../../../../common/ui';
import { FieldSelectorItem } from '../../../../../../common/ui/field-selector-item';
import {
	getDescriptorForNewFieldType,
	getIcon,
	getDescriptionForNewFieldType,
} from '../../../../../../common/utils/fields';
import { useThirdPartyFieldConfigState } from '../../../../../../controllers/import-state';
import { useUIState } from '../../../../../../controllers/ui-state';
import messages from './messages';

type Props = {
	thirdPartyFieldMapping: ThirdPartyFieldMapping;
	onClickGoBack: (event: MouseEvent<Element> | KeyboardEvent<Element>) => void;
};

export const NewCustomFieldsSection = ({
	thirdPartyFieldMapping: { mappedJiraField, compatibleNewFieldTypes, thirdPartyField },
	onClickGoBack,
}: Props) => {
	const { formatMessage } = useIntl();
	const [, { setThirdPartyFieldMapping }] = useThirdPartyFieldConfigState();
	const [, { setUIState }] = useUIState();
	const handleDropdownItemClickFactory = useCallback(
		(newField: Field) => () => {
			setThirdPartyFieldMapping(thirdPartyField.fieldId, {
				...newField,
				fieldName: thirdPartyField.fieldName,
			});
			setUIState({ hasUnsavedFieldMappingChanges: true });
		},
		[setThirdPartyFieldMapping, thirdPartyField.fieldId, thirdPartyField.fieldName, setUIState],
	);

	const availableNewFieldItems = useMemo(
		() =>
			compatibleNewFieldTypes
				.map((newFieldType) => ({
					fieldType: newFieldType,
					fieldName: formatMessage(getDescriptorForNewFieldType(newFieldType)),
					fieldId: null,
				}))
				.map((newField) => {
					const isSelected =
						!mappedJiraField.fieldId && newField.fieldType === mappedJiraField.fieldType;
					const descriptionMessage = getDescriptionForNewFieldType(newField.fieldType);
					const description = descriptionMessage ? formatMessage(descriptionMessage) : undefined;

					return (
						<FieldSelectorItem
							key={newField.fieldType}
							name={formatMessage(getDescriptorForNewFieldType(newField.fieldType))}
							description={description}
							icon={getIcon({ fieldType: newField.fieldType, isSelected })}
							isSelected={isSelected}
							onClick={handleDropdownItemClickFactory(newField)}
						/>
					);
				}),
		[
			compatibleNewFieldTypes,
			formatMessage,
			handleDropdownItemClickFactory,
			mappedJiraField.fieldId,
			mappedJiraField.fieldType,
		],
	);

	return (
		<FocusLock autoFocus>
			<DropdownItemGroup
				testId={`${THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX}.new-custom-fields-section.dropdown-item-group`}
			>
				<GoBackWrapper>
					<Button
						iconBefore={
							<ChevronLeftIcon label="" primaryColor={token('color.icon.subtle', colors.N300)} />
						}
						appearance="subtle"
						spacing="none"
						onClick={onClickGoBack}
						testId={`${THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX}.new-custom-fields-section.button`}
					/>
					<GoBackContent>{formatMessage(messages.goBack)}</GoBackContent>
				</GoBackWrapper>
				<DropdownSectionWrapper>
					<DropdownSectionHeader>
						{formatMessage(messages.selectCustomFieldHeading)}
					</DropdownSectionHeader>
				</DropdownSectionWrapper>
				{availableNewFieldItems}
			</DropdownItemGroup>
		</FocusLock>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoBackWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	padding: `0 ${token('space.100', '8px')}`,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		borderRadius: '50%',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoBackContent = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N300),
	textTransform: 'uppercase',
	font: token('font.body.small'),
	fontWeight: token('font.weight.bold'),
	marginLeft: token('space.100', '8px'),
});
